/**
 * Pre-fetches logo images and converts to data URLs for reliable
 * html-to-image export (avoids cross-origin / caching issues on iOS Safari).
 */

const cache = {};

export async function toDataURL(src) {
  if (cache[src]) return cache[src];
  try {
    const res = await fetch(src);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        cache[src] = reader.result;
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });
  } catch {
    return src; // fallback to original
  }
}

let preloaded = null;

export async function getLogoDataURLs() {
  if (preloaded) return preloaded;
  const [icon, text] = await Promise.all([
    toDataURL('/logo-icon.png'),
    toDataURL('/logo-text.png'),
  ]);
  preloaded = { icon, text };
  return preloaded;
}

// Kick off preload immediately on import
getLogoDataURLs();

/**
 * Wait for every <img> inside `root` to be loaded (has non-zero natural size).
 * Resolves once all images report complete + non-zero dimensions OR errored out,
 * with a timeout safety net. Also attempts a decode() on each so the image is
 * ready to paint before any subsequent html-to-image snapshot.
 */
export async function waitForImages(root, { timeout = 8000 } = {}) {
  if (!root) return;
  const imgs = Array.from(root.querySelectorAll('img'));
  const waitOne = (img) => {
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => { img.removeEventListener('load', done); img.removeEventListener('error', done); resolve(); };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  };
  const all = Promise.all(imgs.map(waitOne));
  const timer = new Promise((resolve) => setTimeout(resolve, timeout));
  await Promise.race([all, timer]);
  await Promise.all(imgs.map((img) => (img.decode ? img.decode().catch(() => {}) : Promise.resolve())));
}
